/**
 * Stable transaction identity for duplicate prevention.
 *
 * v28 hashed date|description|amount|direction|upload_id. Including the upload
 * id meant the same PDF uploaded twice produced different hashes, so every
 * transaction was imported twice. Identical same-day rows inside one statement
 * also collided.
 *
 * The key here depends only on facts about the account, never on the upload:
 *   account | date | direction | amount | running balance after the row
 * The running balance makes otherwise-identical same-day rows distinct and is
 * the same in any statement export that covers the row. When a row has no
 * running balance, the normalised description plus its occurrence number among
 * identical rows is used instead.
 */
import type { ParsedTransaction } from '../parser/types';

export interface DedupeInput {
  /** Stable account identity, e.g. the accounts.id UUID once matched. */
  accountKey: string;
  transactions: ParsedTransaction[];
}

const normalise = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();

export function dedupeKeyMaterial({ accountKey, transactions }: DedupeInput): string[] {
  const seen = new Map<string, number>();
  return transactions.map((tx) => {
    const base = [accountKey, tx.date, tx.direction, tx.amount.toFixed(2)];
    if (tx.balance != null) return [...base, `bal:${tx.balance.toFixed(2)}`].join('|');
    const k = [...base, normalise(tx.description)].join('|');
    const n = (seen.get(k) ?? 0) + 1;
    seen.set(k, n);
    return `${k}|occ:${n}`;
  });
}

export async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function dedupeHashes(input: DedupeInput): Promise<string[]> {
  return Promise.all(dedupeKeyMaterial(input).map(sha256Hex));
}
